import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Copy, Users, KeyRound, LogOut, DoorOpen, Bell, BellOff, Share2, Plus, Check, ChevronsUpDown, Trash2, BookOpen } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { pushSupported, pushPermission, getCurrentSubscription, subscribeToPush, unsubscribeFromPush, sendTestPush } from "@/lib/push";
import { pickColor } from "@/lib/palette";

export default function Profile() {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null); // { group, members } - active group
  const [groups, setGroups] = useState([]); // list of all my groups
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const [pushState, setPushState] = useState({ supported: false, enabled: false, permission: "default", busy: false });

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [me, list] = await Promise.all([api.get("/groups/me"), api.get("/groups")]);
      setData(me.data);
      setGroups(list.data.groups || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
    (async () => {
      const supported = pushSupported();
      const permission = pushPermission();
      let enabled = false;
      if (supported) {
        const sub = await getCurrentSubscription();
        enabled = !!sub && permission === "granted";
      }
      setPushState({ supported, enabled, permission, busy: false });
    })();
  }, [loadAll]);

  const inviteUrl = data?.group
    ? `${window.location.origin}/?invite=${encodeURIComponent(data.group.invite_code)}`
    : "";

  const isOwner = !!(data?.group && user && data.group.owner_id === user.id);

  const copyCode = async () => {
    if (!data?.group?.invite_code) return;
    try {
      await navigator.clipboard.writeText(data.group.invite_code);
      toast.success("Codice copiato!");
    } catch {
      toast.error("Copia non riuscita");
    }
  };

  const copyLink = async () => {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      toast.success("Link invito copiato!");
    } catch {
      toast.error("Copia non riuscita");
    }
  };

  const shareInvite = async () => {
    if (!data?.group) return;
    const text = `Unisciti al gruppo "${data.group.name}" su Hopzigo! Codice: ${data.group.invite_code}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Hopzigo", text, url: inviteUrl });
      } catch (e) {
        // user cancelled - ignore
      }
    } else {
      await copyLink();
    }
  };

  const switchGroup = async (gid) => {
    if (switching || gid === data?.group?.id) return;
    setSwitching(true);
    try {
      await api.post(`/groups/switch/${gid}`);
      await refresh();
      await loadAll();
      toast.success("Gruppo cambiato");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSwitching(false);
    }
  };

  const leave = async () => {
    if (!window.confirm("Vuoi davvero uscire da questo gruppo?")) return;
    try {
      const { data: res } = await api.post("/groups/leave");
      await refresh();
      if (!res.active_group_id) nav("/onboarding");
      else await loadAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteGroup = async () => {
    if (!data?.group) return;
    const name = data.group.name;
    const confirmed = window.confirm(
      `Stai per CANCELLARE il gruppo "${name}". Verranno eliminate TUTTE le corse e tutti i membri verranno rimossi. Questa operazione è irreversibile.\n\nProcedere?`
    );
    if (!confirmed) return;
    const typed = window.prompt(`Per conferma scrivi il nome del gruppo: ${name}`);
    if (typed !== name) {
      toast.error("Nome non corrispondente. Cancellazione annullata.");
      return;
    }
    try {
      await api.delete(`/groups/${data.group.id}`);
      await refresh();
      toast.success("Gruppo cancellato");
      const { data: list } = await api.get("/groups");
      if (!list.groups || list.groups.length === 0) {
        nav("/onboarding");
      } else {
        await loadAll();
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const togglePush = async () => {
    setPushState((s) => ({ ...s, busy: true }));
    try {
      if (pushState.enabled) {
        await unsubscribeFromPush();
        setPushState({ supported: true, enabled: false, permission: pushPermission(), busy: false });
        toast.success("Notifiche disattivate");
      } else {
        await subscribeToPush();
        setPushState({ supported: true, enabled: true, permission: "granted", busy: false });
        toast.success("Notifiche attivate!");
      }
    } catch (e) {
      setPushState((s) => ({ ...s, busy: false, permission: pushPermission() }));
      toast.error(e.message || "Errore notifiche");
    }
  };

  const testPush = async () => {
    try {
      await sendTestPush();
      toast.success("Notifica di prova inviata");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) return <div className="brut-card p-8 text-center font-bold">Caricamento…</div>;

  return (
    <div className="max-w-2xl mx-auto anim-slide-up space-y-5" data-testid="profile-page">
      <div className="pt-2 px-1">
        <p className="text-xs font-semibold uppercase tracking-widest text-[#8A91A8] mb-1">Il tuo profilo</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-[#1A1D29]">{user?.name}</h1>
        <p className="text-[#8A91A8]">{user?.email}</p>
      </div>

      {data?.group && (
        <div className="brut-card p-6 bg-gradient-to-br from-[#1A2A6C] to-[#2D3F8F] text-white border-0">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2 min-w-0">
              <Users className="w-5 h-5 shrink-0" />
              <h2 className="font-heading text-xl font-bold truncate" data-testid="profile-active-group-name">{data.group.name}</h2>
            </div>
            {groups.length > 1 && (
              <span className="brut-chip bg-white/15 text-white border border-white/20">
                <ChevronsUpDown className="w-3 h-3" /> {groups.length} gruppi
              </span>
            )}
          </div>
          <p className="text-xs font-semibold uppercase tracking-widest text-white/70 mb-2">Codice invito</p>
          <div className="flex flex-wrap items-center gap-3 mt-1">
            <div className="font-heading font-bold text-3xl tracking-[0.3em] bg-white text-[#1A2A6C] rounded-2xl px-4 py-2.5 shadow-[0_6px_20px_rgba(0,0,0,0.15)]" data-testid="profile-invite-code">
              {data.group.invite_code}
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={copyCode} className="px-3 py-2.5 rounded-2xl bg-white/15 backdrop-blur border border-white/20 text-white font-semibold hover:bg-white/25 inline-flex items-center gap-1.5 text-sm" data-testid="profile-copy-code">
                <Copy className="w-4 h-4" /> Codice
              </button>
              <button onClick={copyLink} className="px-3 py-2.5 rounded-2xl bg-white/15 backdrop-blur border border-white/20 text-white font-semibold hover:bg-white/25 inline-flex items-center gap-1.5 text-sm" data-testid="profile-copy-link">
                <Copy className="w-4 h-4" /> Link
              </button>
              <button onClick={shareInvite} className="px-3 py-2.5 rounded-2xl bg-white text-[#1A2A6C] font-semibold hover:bg-[#F5F6FA] inline-flex items-center gap-1.5 text-sm shadow-[0_4px_14px_rgba(0,0,0,0.12)]" data-testid="profile-share">
                <Share2 className="w-4 h-4" /> Condividi
              </button>
            </div>
          </div>
          <p className="text-xs text-white/80 mt-3 break-all">{inviteUrl}</p>
        </div>
      )}

      {/* Groups switcher */}
      <div className="brut-card p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-lg font-bold flex items-center gap-2">
            <Users className="w-4 h-4 text-[#1A2A6C]" /> I tuoi gruppi ({groups.length})
          </h3>
          <button onClick={() => nav("/onboarding")} className="px-3 py-2 rounded-xl bg-[#E5E9F5] text-[#1A2A6C] font-semibold text-sm hover:bg-[#CDD3EA] inline-flex items-center gap-1.5" data-testid="profile-add-group">
            <Plus className="w-4 h-4" /> Aggiungi
          </button>
        </div>
        <ul className="space-y-2" data-testid="profile-groups-list">
          {groups.map((g) => {
            const active = g.id === data?.group?.id;
            return (
              <li key={g.id}>
                <button
                  onClick={() => switchGroup(g.id)}
                  disabled={switching}
                  className={`w-full flex items-center gap-3 rounded-2xl p-3 text-left transition-all ${active ? "bg-[#E5E9F5] ring-2 ring-[#1A2A6C]" : "bg-[#F5F6FA] hover:bg-[#E5E9F5]"}`}
                  data-testid={`profile-group-${g.id}`}
                >
                  <div className={`w-10 h-10 grid place-items-center rounded-xl font-heading font-bold ${active ? "bg-[#1A2A6C] text-white" : "bg-white text-[#1A2A6C] border border-[#ECEEF5]"}`}>
                    {g.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold leading-tight truncate">{g.name}</p>
                    <p className="text-xs text-[#8A91A8]">{g.member_count} {g.member_count === 1 ? "membro" : "membri"} · {g.invite_code}</p>
                  </div>
                  {active && <Check className="w-5 h-5 text-[#1A2A6C] shrink-0" />}
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Push notifications */}
      <div className="brut-card p-6">
        <div className="flex items-start gap-3 mb-1">
          <div className="w-11 h-11 grid place-items-center rounded-2xl bg-[#E5E9F5] text-[#1A2A6C] shrink-0">
            {pushState.enabled ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading text-lg font-bold">Notifiche push</h3>
            <p className="text-sm text-[#8A91A8]">
              {!pushState.supported
                ? "Il tuo browser non supporta le notifiche push."
                : pushState.permission === "denied"
                ? "Permesso bloccato. Abilita le notifiche dalle impostazioni del browser."
                : pushState.enabled
                ? "Ricevi avvisi per nuove corse, prenotazioni e cancellazioni."
                : "Attivale per ricevere avvisi su nuove corse, prenotazioni e cancellazioni."}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-3 mt-4">
          <button
            onClick={togglePush}
            disabled={!pushState.supported || pushState.permission === "denied" || pushState.busy}
            className={`brut-btn ${pushState.enabled ? "brut-btn-ghost" : ""}`}
            data-testid="profile-push-toggle"
          >
            {pushState.busy
              ? "Attendi…"
              : pushState.enabled
              ? (<><BellOff className="w-4 h-4" /> Disattiva</>)
              : (<><Bell className="w-4 h-4" /> Attiva notifiche</>)}
          </button>
          {pushState.enabled && (
            <button onClick={testPush} className="brut-btn brut-btn-ghost" data-testid="profile-push-test">
              Invia notifica di prova
            </button>
          )}
        </div>
      </div>

      {/* Members of active group */}
      <div className="brut-card p-6">
        <h3 className="font-heading text-lg font-bold mb-3 flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-[#1A2A6C]" /> Membri di "{data?.group?.name}" ({data?.members?.length || 0})
        </h3>
        <ul className="space-y-2" data-testid="profile-members-list">
          {(data?.members || []).map((m, idx) => {
            const c = pickColor(m.id || idx);
            return (
              <li key={m.id} className="flex items-center gap-3 rounded-2xl bg-[#F5F6FA] p-3" data-testid={`member-row-${m.id}`}>
                <div className="w-10 h-10 grid place-items-center rounded-full font-heading font-bold" style={{ backgroundColor: c.bg, color: c.text }}>
                  {m.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold leading-tight truncate">{m.name} {m.id === user.id && <span className="text-xs text-[#8A91A8]">(tu)</span>}</p>
                  <p className="text-xs text-[#8A91A8] truncate">{m.email}</p>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {isOwner && (
        <div className="brut-card p-5 border-l-4 border-l-[#FF4D5E]">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 grid place-items-center rounded-2xl bg-[#FFE3E3] text-[#FF4D5E] shrink-0">
              <Trash2 className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-heading text-lg font-bold">Cancella gruppo</h3>
              <p className="text-sm text-[#8A91A8]">
                Sei il proprietario di "{data?.group?.name}". Cancellando il gruppo verranno rimosse tutte le corse e i membri.
              </p>
            </div>
          </div>
          <button onClick={deleteGroup} className="mt-4 w-full px-5 py-3 rounded-2xl bg-[#FF4D5E] text-white font-semibold hover:bg-[#E63D4E] shadow-[0_4px_14px_rgba(255,77,94,0.32)] inline-flex items-center justify-center gap-2" data-testid="profile-delete-group">
            <Trash2 className="w-4 h-4" /> Cancella definitivamente il gruppo
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <button onClick={leave} className="brut-btn brut-btn-ghost" data-testid="profile-leave-group">
          <DoorOpen className="w-4 h-4" /> Esci dal gruppo
        </button>
        <button onClick={logout} className="brut-btn" data-testid="profile-logout">
          <LogOut className="w-4 h-4" /> Esci dall'account
        </button>
      </div>

      <button
        onClick={() => window.dispatchEvent(new Event("hopzigo:open-tutorial"))}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 rounded-2xl bg-white border border-[#ECEEF5] hover:border-[#1A2A6C]/30 hover:bg-[#F5F6FA] transition-all"
        data-testid="profile-open-tutorial"
      >
        <span className="flex items-center gap-3 min-w-0">
          <span className="w-10 h-10 grid place-items-center rounded-xl bg-[#1A2A6C]/10 text-[#1A2A6C] shrink-0">
            <BookOpen className="w-5 h-5" />
          </span>
          <span className="text-left min-w-0">
            <span className="block font-heading font-bold text-[#1A1D29]">Come utilizzare l'app</span>
            <span className="block text-xs text-[#8A91A8]">Rivedi il tutorial introduttivo</span>
          </span>
        </span>
        <span className="text-[#1A2A6C] font-bold">›</span>
      </button>

      <a
        href="/hopzigo-termini-condizioni.pdf"
        target="_blank"
        rel="noopener noreferrer"
        className="block text-center text-sm font-semibold text-[#1A2A6C] hover:text-[#FF9E1B] underline underline-offset-4 pt-2"
        data-testid="profile-terms-link"
      >
        Termini e Condizioni
      </a>
    </div>
  );
}
